import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { api, ApiError } from '../api/client'
import { env } from '../env'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LogIn } from 'lucide-react'

interface LocationState {
  from?: { pathname: string }
}

interface TenantInfo {
  slug: string
  displayName: string
  provider: 'google' | 'microsoft' | 'generic' | null
}

export function LoginPage() {
  const { session } = useAuth()
  const location = useLocation()
  const [tenant, setTenant] = useState<TenantInfo | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.auth.tenantInfo()
      .then(setTenant)
      .catch((err) => {
        if (err instanceof ApiError) setError(`${err.status}: ${err.message}`)
        else setError((err as Error).message)
      })
  }, [])

  if (session) {
    const dest = (location.state as LocationState | null)?.from?.pathname ?? '/'
    return <Navigate to={dest} replace />
  }

  const beginUrl = () => {
    const dest = (location.state as LocationState | null)?.from?.pathname ?? '/'
    const q = new URLSearchParams({ redirect_to: dest })
    return `${env.authBaseUrl}/v1/auth/begin?${q.toString()}`
  }

  const providerLabel =
    tenant?.provider === 'google'    ? 'Google' :
    tenant?.provider === 'microsoft' ? 'Microsoft' :
    tenant?.provider === 'generic'   ? 'SSO' :
    'identity provider'

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{tenant ? `${tenant.displayName} Admin` : 'reSet Admin'}</CardTitle>
          <CardDescription>Sign in with your organization's {providerLabel} account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button asChild className="w-full">
            <a href={beginUrl()}>
              <LogIn className="mr-2 h-4 w-4" />
              Log in as admin
            </a>
          </Button>
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
